import { useEffect, useState } from 'react';
import { Plus, Loader2, Ticket, Trash2 } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

/**
 * Platform promo codes — discount codes a customer types into the Rumio Site
 * Builder checkout for rumio.world hosting plans. Super-admin only.
 *
 * Not Stripe promotion codes: the Site Builder checkout already spends its
 * single Checkout-Session discount slot on the add-on intro coupon, so these
 * are validated by the platform and attached to the subscription as plain
 * coupons (see functions/src/services/platform/promo-codes.ts).
 */

type AppliesTo = 'plan_only' | 'plan_and_addons';

interface PromoCode {
  id: string;
  code: string;
  note: string | null;
  percent_off: number;
  duration_months: number;
  applies_to: AppliesTo;
  max_redemptions: number | null;
  times_redeemed: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface FormData {
  code: string;
  percent_off: string;
  duration_months: string;
  applies_to: AppliesTo;
  max_redemptions: string;
  expires_at: string;
  note: string;
}

const INITIAL_FORM: FormData = {
  code: '',
  percent_off: '',
  duration_months: '12',
  applies_to: 'plan_only',
  max_redemptions: '',
  expires_at: '',
  note: '',
};

function describeDiscount(p: PromoCode): string {
  const scope = p.applies_to === 'plan_only' ? 'the plan' : 'plan + partner add-ons';
  const months = p.duration_months === 1 ? '1 month' : `${p.duration_months} months`;
  return `${p.percent_off}% off ${scope} for ${months}`;
}

const PlatformPromos = () => {
  const addToast = useUIStore((s) => s.addToast);
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCodes();
  }, []);

  async function fetchCodes() {
    try {
      setIsLoading(true);
      const res = await api.get<{ codes: PromoCode[] }>('/admin/platform/promos');
      setCodes(res.codes);
    } catch (err: any) {
      addToast(err?.message || 'Failed to load promo codes', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  function openCreate() {
    setForm(INITIAL_FORM);
    setModalOpen(true);
  }

  async function handleSave() {
    const code = form.code.trim().toUpperCase();
    const percent = parseInt(form.percent_off, 10);
    const months = parseInt(form.duration_months, 10);
    if (code.length < 3) {
      addToast('Code must be at least 3 characters', 'error');
      return;
    }
    if (!(percent >= 1 && percent <= 100)) {
      addToast('Percent off must be between 1 and 100', 'error');
      return;
    }
    if (!(months >= 1 && months <= 36)) {
      addToast('Duration must be between 1 and 36 months', 'error');
      return;
    }
    try {
      setIsSaving(true);
      const payload: Record<string, unknown> = {
        code,
        percent_off: percent,
        duration_months: months,
        applies_to: form.applies_to,
      };
      if (form.max_redemptions) payload.max_redemptions = parseInt(form.max_redemptions, 10);
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      if (form.note.trim()) payload.note = form.note.trim();

      await api.post('/admin/platform/promos', payload);
      addToast('Promo code created', 'success');
      setModalOpen(false);
      fetchCodes();
    } catch (err: any) {
      addToast(err?.message || 'Failed to create promo code', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function deactivate(id: string) {
    try {
      await api.delete(`/admin/platform/promos/${id}`);
      addToast('Promo code deactivated', 'success');
      setCodes((prev) => prev.map((c) => (c.id === id ? { ...c, is_active: false } : c)));
    } catch (err: any) {
      addToast(err?.message || 'Failed to deactivate', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-ink">Platform Promo Codes</h1>
          <p className="text-ink-muted mt-1">
            Codes customers enter in the Site Builder checkout for rumio.world hosting plans.
            The discount runs for the chosen number of paid months (monthly billing) or the
            first annual invoice. Deactivating a code stops new redemptions only — existing
            subscribers keep their discount.
          </p>
        </div>
        <Button variant="primary" size="md" onClick={openCreate}>
          <Plus className="w-5 h-5 mr-2" />
          New Code
        </Button>
      </div>

      {codes.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <Ticket className="w-16 h-16 mx-auto mb-4 text-ink-soft" />
            <h3 className="text-lg font-semibold text-ink mb-2">No promo codes yet</h3>
            <p className="text-ink-muted mb-6">
              Create a code to offer a discount on hosting plans.
            </p>
            <Button variant="primary" size="md" onClick={openCreate}>
              <Plus className="w-5 h-5 mr-2" />
              New Code
            </Button>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left text-ink-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Discount</th>
                <th className="px-4 py-3 font-medium">Redemptions</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Note</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {codes.map((c) => (
                <tr key={c.id} className={c.is_active ? '' : 'opacity-50'}>
                  <td className="px-4 py-3 font-mono font-semibold text-ink">{c.code}</td>
                  <td className="px-4 py-3 text-ink-soft">{describeDiscount(c)}</td>
                  <td className="px-4 py-3 text-ink-soft">
                    {c.times_redeemed}
                    {c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-ink-muted max-w-xs truncate" title={c.note ?? ''}>
                    {c.note || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {c.is_active ? (
                      <Badge variant="green">Active</Badge>
                    ) : (
                      <Badge variant="red">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.is_active && (
                      <button
                        onClick={() => deactivate(c.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Deactivate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="New Promo Code">
        <div className="space-y-4">
          <Input
            label="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="FOUNDER15"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Percent off (1-100)"
              type="number"
              value={form.percent_off}
              onChange={(e) => setForm((f) => ({ ...f, percent_off: e.target.value }))}
              placeholder="15"
            />
            <Input
              label="Paid months discounted"
              type="number"
              value={form.duration_months}
              onChange={(e) => setForm((f) => ({ ...f, duration_months: e.target.value }))}
              placeholder="12"
            />
          </div>

          <Select
            label="Applies to"
            value={form.applies_to}
            onChange={(e) => setForm((f) => ({ ...f, applies_to: e.target.value as AppliesTo }))}
            options={[
              { label: 'Hosting plan only (add-ons keep their intro pricing)', value: 'plan_only' },
              { label: 'Hosting plan + partner add-ons (stacks on the intro)', value: 'plan_and_addons' },
            ]}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Max redemptions (optional)"
              type="number"
              value={form.max_redemptions}
              onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
              placeholder="Unlimited"
            />
            <Input
              label="Expires (optional)"
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
            />
          </div>

          <Input
            label="Note (optional)"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Who this is for"
          />

          <p className="text-xs text-ink-muted">
            AI credit top-ups are never discounted — they are billed separately from the
            subscription.
          </p>

          <div className="flex justify-end gap-3 pt-2 border-t border-line">
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isSaving ? 'Creating...' : 'Create Code'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PlatformPromos;
